import axios from "axios";

import constants from "../../constants";

/**
 * Payments with split ("vendas"): the owner's recipient (Pagar.me) and the charging settings
 * (contract: api-keepo-v2's payments endpoints). Only the Keepo's owner; with the feature off
 * every call answers 404 PAYMENTS_DISABLED.
 */

export type PaymentRecipientStatus = "draft" | "pending" | "approved" | "needs_info" | "refused";

export type PaymentRecipient = {
  id: string;
  status: PaymentRecipientStatus;
  statusReason: string | null;
  kycLinkAvailable: boolean;
  personType: "pf" | "pj";
  /** Masked (•••.456.789-••); `null` for a registration not sent yet */
  document: string | null;
  /** `null` for a registration not sent yet */
  legalName: string | null;
  /** "Nubank ••42"; `null` until the bank account is known */
  bankLabel: string | null;
  sharedWith: { id: string; title: string }[];
  /** Another registration may take its place (Trocar recebedor): needs a correction and never sold, or refused */
  replaceable?: boolean;
};

/** An approved recipient of the same person that can be used in another Keepo. */
export type ReusableRecipient = {
  id: string;
  legalName: string | null;
  document: string | null;
  bankLabel: string | null;
};

export type FeesPayer = "owner" | "customer";

type ExampleFees = { pix: number; card: number };

export type PaymentsState = {
  enabled: boolean;
  eligible: boolean;
  account: { agendaOnline: boolean; feesPayer: FeesPayer };
  recipient: PaymentRecipient | null;
  reusable: ReusableRecipient[];
  minSaleCents: number;
  /** What a sale of `priceCents` costs and pays, per who bears the fees. */
  example: { priceCents: number; owner: ExampleFees; customer: ExampleFees };
  /** The one fee the owner sees per method (Pagar.me's and the Keepo's together): basis points and a fixed part. */
  fees: Record<"pix" | "card", { pctBp: number; fixedCents: number }> & {
    boleto?: { pctBp: number; fixedCents: number };
  };
  withdrawFeeCents: number;
  minWithdrawCents: number;
  /** Days a card sale takes to be available for withdrawal (a Pix is at once). */
  cardReleaseDays: number;
};

export type BankInput = {
  code: string;
  name: string;
  branch: string;
  branchDigit: string;
  account: string;
  accountDigit: string;
  type: "checking" | "savings";
};

export type RecipientInput = {
  document: string;
  name: string;
  /** YYYY-MM-DD */
  birthdate: string;
  phone: string;
  occupation: string;
  monthlyIncomeCents: number;
  address: {
    zipCode: string;
    street: string;
    number: string;
    complement: string;
    neighborhood: string;
    city: string;
    state: string;
  };
  bank: BankInput;
};

export type KycLink = { url: string; qrCode: string | null; expiresAt: string | null };

const base = (siteId: string) => `${constants.api}/sites/${encodeURIComponent(siteId)}/payments`;

export const getPayments = async (siteId: string): Promise<PaymentsState> => (await axios.get(base(siteId))).data;

export const updatePayments = async (
  siteId: string,
  changes: { agendaOnline?: boolean; feesPayer?: FeesPayer }
): Promise<PaymentsState> => (await axios.patch(base(siteId), changes)).data;

export const createRecipient = async (siteId: string, input: RecipientInput): Promise<PaymentsState> =>
  (await axios.post(`${base(siteId)}/recipient`, input)).data;

/** Uses, in this Keepo, an approved recipient the owner already has. */
export const reuseRecipient = async (siteId: string, recipientId: string): Promise<PaymentsState> =>
  (await axios.put(`${base(siteId)}/recipient`, { recipientId })).data;

/** The linked registration's current payout account, read from Pagar.me (to start a change from it). */
export const getBankAccount = async (siteId: string): Promise<Omit<BankInput, "name">> =>
  (await axios.get(`${base(siteId)}/bank-account`)).data;

export const changeBankAccount = async (siteId: string, bank: BankInput): Promise<PaymentsState> =>
  (await axios.put(`${base(siteId)}/bank-account`, { bank })).data;

export const createKycLink = async (siteId: string): Promise<KycLink> =>
  (await axios.post(`${base(siteId)}/kyc-link`, {})).data;

/**
 * The 422 `fields` of a failed call (dotted keys: "bank.name", "address.zipCode", "document"),
 * to show next to the form's inputs. Empty when the error carries none.
 */
export const paymentErrorFields = (error: unknown): Record<string, string> => {
  const fields = axios.isAxiosError(error) ? error.response?.data?.fields : undefined;
  return fields && typeof fields === "object" && !Array.isArray(fields) ? fields : {};
};

/** The linked registration's balance (shared by every Keepo linked to it), the withdrawal's fee and minimum. */
export type PaymentBalance = {
  availableCents: number;
  waitingCents: number;
  withdrawFeeCents: number;
  minWithdrawCents: number;
  sharedWith: { id: string; title: string }[];
  /** This Keepo's sales still paid this month (Brazil's): what customers paid and what the owner gets. */
  month: { salesCents: number; netCents: number; count: number };
};

/**
 * A withdrawal: `pending` until Pagar.me takes it, `processing` then `paid` or `failed`; `review` is one a person must
 * check (it may have left: it stays open and blocks the next).
 */
export type WithdrawalStatus = "pending" | "processing" | "paid" | "failed" | "review";

export type PaymentWithdrawal = {
  id: string;
  status: WithdrawalStatus;
  amountCents: number;
  feeCents: number;
  netCents: number;
  requestedAt: string;
};

/**
 * One line of the Keepo's history. A sale: what the customer paid, the fees and the owner's net (status `paid` or
 * `refunded`); a refund: what went back (`netCents` negative); a withdrawal: the amount, its fee and what reached the bank.
 */
export type PaymentHistoryItem = {
  type: "sale" | "refund" | "withdrawal";
  id: string;
  at: string;
  /** How the sale was (or is being) paid; null for a withdrawal, or while the customer hasn't chosen */
  method?: "pix" | "card" | "boleto" | null;
  customer?: string | null;
  service?: string | null;
  /** The booking a sale paid for (its uuid): a paid sale is refunded through it */
  bookingId?: string | null;
  paidCents?: number | null;
  feesCents?: number | null;
  netCents: number;
  status: string;
};

/** A page of the history, the newest first; `nextBefore` asks for the next one (null: the last). */
export type PaymentHistoryPage = { items: PaymentHistoryItem[]; nextBefore: string | null };

export const getBalance = async (siteId: string): Promise<PaymentBalance> =>
  (await axios.get(`${base(siteId)}/balance`)).data;

/** Withdraws `amountCents` (what leaves the balance; the fee comes out of it). */
export const withdraw = async (siteId: string, amountCents: number): Promise<PaymentWithdrawal> =>
  (await axios.post(`${base(siteId)}/withdrawals`, { amountCents })).data;

/** The history's search and filters: lists go comma-separated; dates are YYYY-MM-DD, both days included. */
export type PaymentHistoryFilters = { q?: string; types?: string[]; statuses?: string[]; from?: string; to?: string };

export const getHistory = async (
  siteId: string,
  before?: string | null,
  filters: PaymentHistoryFilters = {}
): Promise<PaymentHistoryPage> => {
  const params: Record<string, string> = {};
  if (before) params.before = before;
  if (filters.q?.trim()) params.q = filters.q.trim();
  if (filters.types?.length) params.types = filters.types.join(",");
  if (filters.statuses?.length) params.statuses = filters.statuses.join(",");
  if (filters.from) params.from = filters.from;
  if (filters.to) params.to = filters.to;
  return (await axios.get(`${base(siteId)}/history`, { params })).data;
};
